import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  ReloadOutlined,
  CheckOutlined,
  CloseOutlined,
  StopOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  CarOutlined,
  SearchOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import { getAllPedidos, confirmarPago, rechazarPago, cancelarPedido, marcarEntregado } from '../../../store/MSCommerce/Pedidos/thunks';
import { getProducts } from '../../../store/MSCommerce/Productos/thunks';
import type { PedidoResponse } from '../../../store/MSCommerce/Pedidos/types';
import type { ProductosResponse } from '../../../store/MSCommerce/Productos/types';
import { isAdmin } from '../../../Services/auth';
import styles from './Reservas.module.scss';

type EstadoPedido = 'EN_REVISION' | 'CONFIRMADO' | 'ENTREGADO' | 'RECHAZADO' | 'CANCELADO';
type Tab = 'TODOS' | EstadoPedido;

const TABS: { key: Tab; label: string }[] = [
  { key: 'TODOS', label: 'Todas' },
  { key: 'EN_REVISION', label: 'En revisión' },
  { key: 'CONFIRMADO', label: 'Confirmada' },
  { key: 'ENTREGADO', label: 'Entregada' },
  { key: 'RECHAZADO', label: 'Rechazada' },
  { key: 'CANCELADO', label: 'Cancelada' },
];

const STOCK_BAJO_UMBRAL = 5;

const ESTADO_LABEL: Record<string, string> = {
  PENDIENTE: 'Pendiente',
  EN_REVISION: 'En revisión',
  CONFIRMADO: 'Confirmada',
  ENTREGADO: 'Entregada',
  RECHAZADO: 'Rechazada',
  CANCELADO: 'Cancelada',
};

const TIPO_ENTREGA_LABEL: Record<string, string> = {
  PUNTO_ENTREGA: 'Punto de entrega',
  DOMICILIO: 'Envío a domicilio',
  NACIONAL: 'Envío nacional',
};

const getCodigoCorto = (id: string) => `GS-${id.slice(0, 8).toUpperCase()}`;

const getAbono = (pedido: PedidoResponse) => pedido.pagos
  .filter(pago => pago.status === 'EN_REVISION' || pago.status === 'APROBADO')
  .reduce((total, pago) => total + (pago.montoPagado ?? 0), 0);

const getSaldoPendiente = (pedido: PedidoResponse) =>
  Math.max(0, Math.round((pedido.total - getAbono(pedido)) * 100) / 100);

const Reservas: React.FC = () => {
  const dispatch = useDispatch<any>();
  const history = useHistory();
  const admin = isAdmin();

  const [pedidos, setPedidos] = useState<PedidoResponse[]>([]);
  const [productos, setProductos] = useState<ProductosResponse[]>([]);
  const [tab, setTab] = useState<Tab>('TODOS');
  const [refreshing, setRefreshing] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const cargar = async () => {
    setRefreshing(true);
    const [pedidosData, productosData] = await Promise.all([
      dispatch(getAllPedidos()),
      dispatch(getProducts()),
    ]);
    setPedidos(Array.isArray(pedidosData) ? pedidosData : []);
    setProductos(Array.isArray(productosData) ? productosData : []);
    setRefreshing(false);
  };

  useEffect(() => {
    if (!admin) {
      history.replace('/');
      return;
    }
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!admin) return null;

  const handleConfirmar = async (pagoId: string) => {
    await dispatch(confirmarPago(pagoId));
    cargar();
  };

  const handleRechazar = async (pagoId: string) => {
    if (!window.confirm('¿Rechazar este comprobante? El stock se liberará.')) return;
    await dispatch(rechazarPago(pagoId));
    cargar();
  };

  const handleCancelar = async (pedidoId: string) => {
    if (!window.confirm('¿Cancelar esta reserva? El stock se liberará.')) return;
    await dispatch(cancelarPedido(pedidoId));
    cargar();
  };

  const handleEntregar = async (pedidoId: string) => {
    if (!window.confirm('¿Marcar esta reserva como entregada?')) return;
    await dispatch(marcarEntregado(pedidoId));
    cargar();
  };

  const pedidosFiltrados = pedidos.filter(p => {
    const enTab = tab === 'TODOS' || p.status === tab;
    if (!enTab) return false;
    if (!busqueda.trim()) return true;
    const consulta = busqueda.trim().toLowerCase();
    return [
      getCodigoCorto(p.id),
      p.entrega?.contactoNombre,
      p.entrega?.contactoPhone,
      p.entrega?.ciudad,
    ].some(valor => valor?.toLowerCase().includes(consulta));
  });

  const porRevisar = pedidos.filter(p => p.status === 'EN_REVISION').length;
  const porEntregar = pedidos.filter(p => p.status === 'CONFIRMADO').length;
  const stockBajo = productos
    .filter(producto => producto.status !== 'INACTIVO' && producto.stock <= STOCK_BAJO_UMBRAL)
    .sort((a, b) => a.stock - b.stock);

  return (
    <div className={styles.reservas}>
      <div className={styles.reservas__header}>
        <div>
          <p className={styles.reservas__subtitle}>GLOW SKIN · ADMIN</p>
          <h1 className={styles.reservas__title}>Reservas</h1>
        </div>
        <div className={styles.reservas__headerRight}>
          <div className={styles.reservas__searchBox}>
            <SearchOutlined />
            <input
              className={styles.reservas__searchInput}
              placeholder="Buscar por código, cliente o teléfono"
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
            />
          </div>
          <button className={styles.reservas__refreshBtn} onClick={cargar}>
            <ReloadOutlined spin={refreshing} /> Recargar
          </button>
        </div>
      </div>

      <div className={styles.reservas__metrics}>
        <button
          type="button"
          className={`${styles.reservas__metric} ${styles['reservas__metric--attention']}`}
          onClick={() => setTab('EN_REVISION')}
        >
          <span className={styles.reservas__metricIcon}><ClockCircleOutlined /></span>
          <span>
            <strong>{porRevisar}</strong>
            <small>Por revisar</small>
          </span>
        </button>
        <button
          type="button"
          className={styles.reservas__metric}
          onClick={() => setTab('CONFIRMADO')}
        >
          <span className={styles.reservas__metricIcon}><CarOutlined /></span>
          <span>
            <strong>{porEntregar}</strong>
            <small>Pendientes de entrega</small>
          </span>
        </button>
        <div className={`${styles.reservas__metric} ${styles['reservas__metric--warning']}`}>
          <span className={styles.reservas__metricIcon}><WarningOutlined /></span>
          <span>
            <strong>{stockBajo.length}</strong>
            <small>Productos con stock bajo</small>
          </span>
        </div>
        <div className={styles.reservas__metric}>
          <span className={styles.reservas__metricIcon}><ShoppingOutlined /></span>
          <span>
            <strong>{pedidos.length}</strong>
            <small>Reservas registradas</small>
          </span>
        </div>
      </div>

      {stockBajo.length > 0 && (
        <div className={styles.reservas__stockPanel}>
          <div className={styles.reservas__stockHeader}>
            <div>
              <p className={styles.reservas__colLabel}>Inventario</p>
              <h2 className={styles.reservas__stockTitle}>Productos que necesitan reposición</h2>
            </div>
            <button type="button" className={styles.reservas__stockLink} onClick={() => history.push('/products')}>
              Ver catálogo
            </button>
          </div>
          <div className={styles.reservas__stockList}>
            {stockBajo.slice(0, 6).map(producto => (
              <div key={producto.id} className={styles.reservas__stockItem}>
                <span>{producto.nombre}</span>
                <strong>{producto.stock} {producto.stock === 1 ? 'unidad' : 'unidades'}</strong>
              </div>
            ))}
          </div>
          {stockBajo.length > 6 && (
            <p className={styles.reservas__stockMore}>Hay {stockBajo.length - 6} productos más con stock bajo.</p>
          )}
        </div>
      )}

      <div className={styles.reservas__tabs}>
        {TABS.map(t => (
          <button
            key={t.key}
            className={`${styles.reservas__tab} ${tab === t.key ? styles['reservas__tab--active'] : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            <span className={styles.reservas__tabCount}>
              {t.key === 'TODOS' ? pedidos.length : pedidos.filter(p => p.status === t.key).length}
            </span>
          </button>
        ))}
      </div>

      <div className={styles.reservas__list}>
        {pedidosFiltrados.length === 0 ? (
          <p className={styles.reservas__empty}>No hay reservas en esta categoría.</p>
        ) : (
          pedidosFiltrados.map(pedido => {
            const pagoActual = pedido.pagos.find(p => p.status === 'EN_REVISION')
              ?? pedido.pagos.find(p => p.status === 'APROBADO')
              ?? pedido.pagos[pedido.pagos.length - 1];
            const abono = getAbono(pedido);
            const saldoPendiente = getSaldoPendiente(pedido);

            return (
              <div key={pedido.id} className={styles.reservas__card}>
                <div className={styles.reservas__cardTop}>
                  <div>
                    <span className={styles.reservas__codigo}>{getCodigoCorto(pedido.id)}</span>
                    <span className={`${styles.reservas__estado} ${styles[`reservas__estado--${pedido.status?.toLowerCase()}`]}`}>
                      {pedido.status ? (ESTADO_LABEL[pedido.status] ?? pedido.status) : ''}
                    </span>
                  </div>
                  <span className={styles.reservas__fecha}>
                    {new Date(pedido.createdAt).toLocaleString('es-EC', { dateStyle: 'medium', timeStyle: 'short' })}
                  </span>
                </div>

                <div className={styles.reservas__cardBody}>
                  <div className={styles.reservas__col}>
                    <p className={styles.reservas__colLabel}>Cliente</p>
                    <p className={styles.reservas__clienteName}>{pedido.entrega?.contactoNombre ?? '—'}</p>
                    {pedido.entrega?.contactoPhone && (
                      <a
                        className={styles.reservas__clientePhone}
                        href={`https://wa.me/593${pedido.entrega.contactoPhone.replace(/^0/, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <PhoneOutlined /> {pedido.entrega.contactoPhone}
                      </a>
                    )}
                    <p className={styles.reservas__entrega}>
                      <EnvironmentOutlined /> {pedido.entrega ? (TIPO_ENTREGA_LABEL[pedido.entrega.tipo] ?? pedido.entrega.tipo) : '—'}
                    </p>
                    {pedido.entrega?.direccion && (
                      <p className={styles.reservas__direccion}>
                        {pedido.entrega.direccion}{pedido.entrega.ciudad ? `, ${pedido.entrega.ciudad}` : ''}
                        {pedido.entrega.referencia ? ` (${pedido.entrega.referencia})` : ''}
                      </p>
                    )}
                  </div>

                  <div className={styles.reservas__col}>
                    <p className={styles.reservas__colLabel}>Productos</p>
                    <ul className={styles.reservas__productos}>
                      {pedido.detalles.map((d, i) => (
                        <li key={i}>{d.nombreProducto} × {d.cantidad} — ${d.total.toFixed(2)}</li>
                      ))}
                    </ul>
                    <p className={styles.reservas__total}>Total: ${pedido.total.toFixed(2)}</p>

                    {pedido.pagos.length > 0 && (
                      <div className={styles.reservas__pago}>
                        <p className={styles.reservas__pagoLinea}>
                          Abonado: ${abono.toFixed(2)}
                        </p>
                        {saldoPendiente > 0 ? (
                          <p className={styles.reservas__saldoPendiente}>
                            Falta por pagar: ${saldoPendiente.toFixed(2)}
                          </p>
                        ) : (
                          <p className={styles.reservas__saldoCompleto}><CheckCircleOutlined /> Pago completo</p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className={styles.reservas__col}>
                    <p className={styles.reservas__colLabel}>Comprobante</p>
                    {pagoActual?.urlComprobante ? (
                      <img
                        className={styles.reservas__comprobante}
                        src={pagoActual.urlComprobante}
                        alt="Comprobante de pago"
                        loading="lazy"
                        onClick={() => setLightboxUrl(pagoActual.urlComprobante!)}
                      />
                    ) : (
                      <p className={styles.reservas__sinComprobante}>Aún no ha subido comprobante</p>
                    )}
                  </div>
                </div>

                <div className={styles.reservas__cardActions}>
                  {pedido.status === 'EN_REVISION' && pagoActual && (
                    <>
                      <button className={styles.reservas__confirmBtn} onClick={() => handleConfirmar(pagoActual.id)}>
                        <CheckOutlined /> Confirmar pago
                      </button>
                      <button className={styles.reservas__rejectBtn} onClick={() => handleRechazar(pagoActual.id)}>
                        <CloseOutlined /> Rechazar
                      </button>
                    </>
                  )}
                  {(pedido.status === 'PENDIENTE' || pedido.status === 'EN_REVISION') && (
                    <button className={styles.reservas__cancelBtn} onClick={() => handleCancelar(pedido.id)}>
                      <StopOutlined /> Cancelar reserva
                    </button>
                  )}
                  {pedido.status === 'CONFIRMADO' && (
                    <button className={styles.reservas__entregarBtn} onClick={() => handleEntregar(pedido.id)}>
                      <CarOutlined /> Marcar como entregado
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {lightboxUrl && (
        <div className={styles.reservas__lightbox} onClick={() => setLightboxUrl(null)}>
          <button
            type="button"
            className={styles.reservas__lightboxClose}
            onClick={() => setLightboxUrl(null)}
            title="Cerrar"
          >
            <CloseOutlined />
          </button>
          <img src={lightboxUrl} alt="Comprobante ampliado" className={styles.reservas__lightboxImg} />
        </div>
      )}
    </div>
  );
};

export default Reservas;
